import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";
import SectionGlow from "./SectionGlow";
import { ease, duration } from "@/lib/motion";

const steps = [
  {
    badge: "Day 1",
    title: "Discovery Call",
    desc: "30-minute call to lock in goals, scope, and timeline. You'll know exactly what we're building before we hang up.",
  },
  {
    badge: "Days 2–4",
    title: "Design & Build",
    desc: "I design and build the full site, then send a working preview for your feedback. Revisions come back the same day.",
  },
  {
    badge: "Days 5–7",
    title: "Review & Launch",
    desc: "Final polish, mobile testing, SEO setup, and go-live. Most clients are live within a week of the first call.",
  },
];

const ProcessSection = () => {
  const isMobile = useIsMobile();

  return (
    <section
      id="process"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-charcoal relative overflow-hidden lg:pt-[var(--navbar-height,4.75rem)]"
      style={{ zIndex: 6 }}
      data-dark-section
    >
      <SectionGlow placement="bottom-left" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-0">
        <motion.div
          initial={{ opacity: 0, y: isMobile ? 0 : 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: duration.smooth, ease: ease.smooth }}
          className="text-center max-w-2xl mx-auto mb-10 md:mb-20"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-3 md:text-sm">How It Works</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mb-4 font-bold leading-[1.1]">
            Designed, Built, and <span className="text-accent-sweep">Launched</span> in 7 Days.
          </h2>
          <p className="text-cream/60 leading-relaxed md:text-lg">
            Three steps. One week. No drawn-out timeline.
          </p>
        </motion.div>

        {isMobile ? (
          <motion.div
            className="grid gap-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
          >
            {steps.map((step) => (
              <div key={step.badge} className="relative text-center px-4">
                <div className="relative z-10 mx-auto mb-3 inline-flex items-center gap-1.5 rounded-full border border-sage/30 bg-sage/10 px-3 py-1">
                  <span className="text-xs font-semibold text-sage">{step.badge}</span>
                </div>
                <h3 className="font-heading text-xl leading-tight text-cream mb-2">{step.title}</h3>
                <p className="text-sm text-cream/60 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </motion.div>
        ) : (
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6 md:gap-0 relative">
            {/* Connector line through circle centers */}
            <div className="hidden md:block absolute top-[1.75rem] left-[calc(16.67%+1.75rem)] right-[calc(16.67%+1.75rem)] h-[1px] bg-gradient-to-r from-sage/30 via-sage/30 to-sage/30" />
            {steps.map((step, i) => (
              <motion.div
                key={step.badge}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: duration.smooth, delay: i * 0.08, ease: ease.smooth }}
                className="relative text-center px-4 md:px-8 group"
              >
                <div className="relative z-10 mx-auto mb-5 h-14 w-14 rounded-full border border-sage/30 bg-charcoal flex items-center justify-center transition-[border-color,background-color] duration-300 group-hover:border-primary/60 group-hover:bg-primary/10">
                  <span className="font-heading text-xs font-bold text-sage transition-colors duration-300 group-hover:text-primary leading-none">{step.badge}</span>
                </div>
                <h3 className="font-heading text-xl md:text-2xl leading-tight text-cream mb-3">{step.title}</h3>
                <p className="text-sm text-cream/60 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProcessSection;
